'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useLearningSource, type Enrollment } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import {
  AnonymousFallback,
  ButtonSkeleton,
  IslandKeyframes,
  ToggleErrorCaption,
  toggleActive,
  toggleError,
  toggleInactive,
} from './_shared';
import { useToggleErrorMessage } from './useToggleErrorMessage';

/**
 * Enroll / unenroll button for a course. Uses
 * {@code LearningSource.enrollments} (optional sub-adapter).
 */
export interface EnrollButtonProps {
  courseSlug: string;
  enrollLabel?: string;
  enrolledLabel?: string;
  signInHref?: string;
}

export function EnrollButton({
  courseSlug,
  enrollLabel,
  enrolledLabel,
  signInHref,
}: EnrollButtonProps) {
  const user = useCurrentUser<{ name?: string }>();
  const learning = useLearningSource();
  const [toggling, setToggling] = useState(false);
  const [optimistic, setOptimistic] = useState<Enrollment | null | undefined>(undefined);
  const { t } = useTranslation('site-builder-blocks');
  const { error, setError, clearError } = useToggleErrorMessage();

  const state = useAsyncResource<Enrollment | { __none: true }>(async () => {
    if (!user || !learning?.enrollments) return null;
    const list = await learning.enrollments.list();
    const found = list.find((e) => e.courseSlug === courseSlug);
    return found ?? { __none: true };
  }, [user, learning, courseSlug]);

  if (!user)
    return (
      <>
        <IslandKeyframes />
        <AnonymousFallback
          variant="button"
          prompt={t('islands.signInToEnroll')}
          {...(signInHref ? { signInHref } : {})}
        />
      </>
    );
  if (!learning?.enrollments) return null;
  if (state.kind === 'loading')
    return (
      <>
        <IslandKeyframes />
        <ButtonSkeleton width="140px" />
      </>
    );

  const enrolled =
    optimistic === undefined
      ? state.kind === 'loaded' && !('__none' in state.data)
      : optimistic !== null;

  const handle = async () => {
    if (toggling) return;
    // Clear any stale error from the previous failure so the new
    // attempt starts with a clean state.
    clearError();
    setToggling(true);
    try {
      if (enrolled) {
        const ok = await learning.enrollments!.unenroll(courseSlug);
        if (ok) setOptimistic(null);
      } else {
        const res = await learning.enrollments!.enroll(courseSlug);
        setOptimistic(res);
      }
    } catch (err) {
      // Roll back optimistic state and surface the failure inline. No
      // global toast — islands ship into hosts that may not provide one.
      setOptimistic(undefined);

      console.error('[EnrollButton] toggle failed', err);
      setError(t('islands.toggleFailed'));
    } finally {
      setToggling(false);
    }
  };

  if (state.kind === 'error')
    return (
      <button type="button" onClick={() => location.reload()} style={toggleError}>
        {t('islands.enroll.retry')}
      </button>
    );

  return (
    <>
      <button
        type="button"
        onClick={handle}
        disabled={toggling}
        aria-pressed={enrolled}
        style={enrolled ? toggleActive : toggleInactive}
      >
        {enrolled
          ? (enrolledLabel ?? t('islands.enroll.enrolled'))
          : (enrollLabel ?? t('islands.enroll.enroll'))}
      </button>
      {error && <ToggleErrorCaption message={error} />}
    </>
  );
}
